import React, { FC, useState } from 'react';

import { MetadataAttributeDatatypeEnum } from '@ariksa/inventory-core/api';
import { HStack, Link, Text, Tooltip, Wrap, WrapItem } from '@chakra-ui/react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { isArray, isPlainObject, isString, map } from 'lodash';
import { useNavigate } from 'react-router-dom';

import { PlusCountTag } from 'components/DataDisplay/Tag/PlusCountTag';
import { GraphIcon } from 'components/Icons';
import { useActiveResourceActions } from 'containers/ActiveCloudResource/hooks/useActiveResourceActions';
import { securityGraphRedirectUrl } from 'containers/Visibility/SecurityGraphNext/utils/securityGraphRedirectUrl';

import { DynamicAttribute, dateWarning, isUrl, toBoolean } from './utils';

dayjs.extend(relativeTime);

const CHIP_LIMIT = 5;

const warningColor = {
  expired: 'critical',
  soon: 'high',
  stale: 'medium',
};

const isDatetime = (attribute: DynamicAttribute) =>
  attribute.datatype === MetadataAttributeDatatypeEnum.Datetime ||
  attribute.warn_within_days !== undefined ||
  attribute.warn_after_days !== undefined;

const RelativeTime: FC<{ attribute: DynamicAttribute }> = ({ attribute }) => {
  const value = attribute.attribute_value as string;
  const date = dayjs(value);
  if (!date.isValid()) return <Text>{value}</Text>;
  const warning = dateWarning(attribute);
  return (
    <Tooltip label={date.format('lll')} hasArrow placement="top">
      <Text
        as="span"
        color={warning ? warningColor[warning] : undefined}
        fontWeight={warning ? 600 : undefined}
      >
        {warning === 'expired' ? `expired ${date.fromNow()}` : date.fromNow()}
      </Text>
    </Tooltip>
  );
};

const Chips: FC<{ values: unknown[] }> = ({ values }) => {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? values : values.slice(0, CHIP_LIMIT);
  return (
    <Wrap spacing={1}>
      {map(shown, (value, index) => (
        <WrapItem key={index}>
          <Text
            px={2}
            py="1px"
            borderRadius="full"
            bg="gray.100"
            // Chip text inherits the value font size, like MetadataField values.
            // Show the full value (wrapping) instead of truncating with "...".
            wordBreak="break-word"
          >
            {String(value)}
          </Text>
        </WrapItem>
      ))}
      {values.length > CHIP_LIMIT && (
        <WrapItem>
          {/* Use the shared PlusCountTag for the hidden count, like other
              lists in the app; "show less" stays a text toggle. */}
          {expanded ? (
            // "show less" uses sm to sit with the chip values.
            <Text
              as="button"
              fontSize="sm"
              color="primary"
              onClick={() => setExpanded(false)}
            >
              show less
            </Text>
          ) : (
            // Match the chip shape; no tooltip since clicking shows all values.
            <PlusCountTag
              count={values.length - CHIP_LIMIT}
              px={2}
              borderRadius="full"
              onClick={() => setExpanded(true)}
            />
          )}
        </WrapItem>
      )}
    </Wrap>
  );
};

interface LinkedValue {
  value: string;
  redirect_resource_uuid?: string;
  redirect_resource_account?: string;
}

const LinkedValues: FC<{ attribute: DynamicAttribute; provider?: string }> = ({
  attribute,
  provider,
}) => {
  const { updateActiveResource } = useActiveResourceActions();
  const navigate = useNavigate();
  const values = (attribute.attribute_value ?? []) as LinkedValue[];
  return (
    <Wrap spacing={2}>
      {map(values, (item, index) => {
        const linked =
          !!item.redirect_resource_uuid && !!item.redirect_resource_account;
        return (
          <WrapItem key={index}>
            <HStack spacing={1}>
              {linked ? (
                <Text
                  as="u"
                  color="primary"
                  cursor="pointer"
                  onClick={() =>
                    updateActiveResource({
                      resourceType: attribute.redirect_resource_type!,
                      cloudProvider: provider as any,
                      uuid: item.redirect_resource_uuid!,
                      accountId: item.redirect_resource_account!,
                    })
                  }
                >
                  {item.value}
                </Text>
              ) : (
                <Text>{item.value}</Text>
              )}
              {linked && (
                <Tooltip label="Show in security graph" hasArrow>
                  <Text
                    as="button"
                    color="gray.400"
                    _hover={{ color: 'primary' }}
                    aria-label={`Show ${item.value} in security graph`}
                    onClick={() =>
                      navigate(
                        securityGraphRedirectUrl({
                          resource_id: item.value,
                          resource_type: attribute.redirect_resource_type,
                          source_resource_uuid: item.redirect_resource_uuid,
                          account_id: item.redirect_resource_account,
                          map_type: 'access',
                        }),
                      )
                    }
                  >
                    <GraphIcon w={3} h={3} />
                  </Text>
                </Tooltip>
              )}
            </HStack>
          </WrapItem>
        );
      })}
    </Wrap>
  );
};

export const SummaryValue: FC<{
  attribute: DynamicAttribute;
  provider?: string;
}> = ({ attribute, provider }) => {
  const value = attribute.attribute_value;
  if (attribute.hyperlink && isArray(value) && value.every(isPlainObject)) {
    return <LinkedValues attribute={attribute} provider={provider} />;
  }
  if (isDatetime(attribute) && isString(value)) {
    return <RelativeTime attribute={attribute} />;
  }
  if (isArray(value)) return <Chips values={value} />;
  if (isUrl(value)) {
    return (
      <Link href={value} isExternal color="primary" wordBreak="break-all">
        {value}
      </Link>
    );
  }
  const asBoolean = toBoolean(value);
  if (asBoolean !== undefined) return <Text>{asBoolean ? 'Yes' : 'No'}</Text>;
  return <Text wordBreak="break-word">{String(value)}</Text>;
};
